/**
 * Get hours until midnight in a specific timezone (reckoning time)
 */
export function getHoursUntilMidnight(timezone: string): number {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      hour12: false,
    });
    const currentHour = parseInt(formatter.format(now), 10);
    return currentHour === 0 ? 24 : 24 - currentHour;
  } catch {
    return 24;
  }
}

/**
 * Format a time in a specific timezone
 */
export function formatLocalTime(
  timezone: string,
  date: Date = new Date(),
): string | null {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * Format a date for display.
 * Parses YYYY-MM-DD as local date to avoid UTC timezone shift issues.
 */
export function formatDate(dateString: string): string {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
}

/**
 * Generate a short name badge from a hero name.
 * Algorithmically shortens to ~maxLength characters per row.
 *
 * Strategy (per row / single-word piece):
 * 1. If piece is maxLength chars or less, use as-is (uppercase)
 * 2. If piece has multiple words, use first letter of each (up to maxLength)
 * 3. If single word, strip vowels; if still too long, take first maxLength chars
 *
 * When rows > 1 and the name contains word-break characters (spaces, dashes,
 * underscores, dots), each word-piece is processed independently as a single
 * row, up to `rows` rows total.
 *
 * Returns an array of strings (length === rows when multi-row, or [result]
 * when rows === 1).
 *
 * Examples (maxLength=4, rows=1):
 * - "Sir Lancelot"    -> ["SL"]
 * - "The Dark Knight" -> ["TDK"]
 * - "Alexandra"       -> ["ALXR"]
 * - "Bob"             -> ["BOB"]
 * - "Christopher"     -> ["CHRS"]
 *
 * Examples (maxLength=4, rows=2):
 * - "Scout Armor"     -> ["SCUT", "ARMR"]
 * - "Pink Witch"      -> ["PNK", "WTCH"]
 * - "Battleaxe"       -> ["BTTL"]   (single word, one row)
 */
export function getNameBadge(
  name: string,
  maxLength: number = 4,
  rows: number = 1,
): string[] {
  if (!name) return [""];

  const trimmed = name.trim();

  function shortenSinglePiece(piece: string): string {
    if (piece.length <= maxLength) {
      return piece.toUpperCase();
    }

    const words = piece.split(/\s+/).filter(Boolean);

    if (words.length > 1) {
      return words
        .slice(0, maxLength)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    }

    const word = words[0];
    const consonants = word.replace(/[aeiou]/gi, "");

    if (consonants.length >= maxLength) {
      return consonants.slice(0, maxLength).toUpperCase();
    }

    return word.slice(0, maxLength).toUpperCase();
  }

  if (rows > 1) {
    const pieces = trimmed.split(/[\s\-_.]+/).filter(Boolean);
    if (pieces.length > 1) {
      return pieces.slice(0, rows).map(shortenSinglePiece);
    }
  }

  return [shortenSinglePiece(trimmed)];
}

